import { type ColDef } from "ag-grid-enterprise";

import type UserTabPermissions from "types/UserTabPermissions/UserTabPermissions";

import { embeddedImagesHtmlLength } from "utilities/form/yup";

import { LineItemConstants } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import {
    type BidPackageDraftDeadline,
    type IBidPackageDraft,
    type IBidPackageDraftLineItem,
    type IBidPackageSuggestion,
    type IBidPackageSuggestionResponse,
    type IRefineBidPackagesMessage,
    type ISubBidInsight,
} from "./AiBidPackageSuggestions.types";

// Same limits as the bid package screen's validators.
export const titleMaxLength = 50;
export const scopeMaxLength = 200000;

// Matches the bid package Responses grid: drag to resize, but no sorting or header menus.
export const gridColumnDefaults: ColDef = {
    resizable: true,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressHeaderFilterButton: true,
};

// Mirrors the context endpoint's gates, so users it would reject are never offered the flow.
export const canSuggestBidPackages = (permissions: UserTabPermissions | null | undefined) =>
    Boolean(permissions?.bidding.canAdd && permissions.estimateGeneralItems.canView);

export const getPackageEstimateTotal = (draft: IBidPackageDraft): number =>
    draft.lineItems.reduce(
        (sum, line) => sum + (line.quantity ?? 0) * (line.estimatedUnitCost ?? 0),
        0,
    );

export const updateLineItem = (
    draft: IBidPackageDraft,
    index: number,
    changes: Partial<IBidPackageDraftLineItem>,
): IBidPackageDraft => ({
    ...draft,
    lineItems: draft.lineItems.map((line, i) => (i === index ? { ...line, ...changes } : line)),
});

export const removeLineItem = (draft: IBidPackageDraft, index: number): IBidPackageDraft => ({
    ...draft,
    lineItems: draft.lineItems.filter((_, i) => i !== index),
});

const isOutOfRange = (value: number | null, { MIN, MAX }: { MIN: number; MAX: number }) =>
    value !== null && (value < MIN || value > MAX);

export interface IDraftError {
    field: "title" | "scope" | "lineItems";
    message: string;
}

/** Why the draft can't be created as a bid package; empty when it can. */
export const getDraftErrors = (draft: IBidPackageDraft): IDraftError[] => {
    const errors: IDraftError[] = [];
    const title = draft.title.trim();
    if (!title) {
        errors.push({ field: "title", message: "Title is required." });
    } else if (title.length > titleMaxLength) {
        errors.push({
            field: "title",
            message: `Title can't be over ${titleMaxLength} characters.`,
        });
    }
    const scope = draft.description.trim();
    if (scope.length - embeddedImagesHtmlLength(scope) > scopeMaxLength) {
        errors.push({
            field: "scope",
            message: "Scope of work can't be over 200,000 characters.",
        });
    }
    const lines = draft.lineItems;
    const { UnitType, Quantity, Currency } = LineItemConstants;
    const lineError = (message: string) => errors.push({ field: "lineItems", message });
    if (!draft.isFlatFee && lines.length === 0) {
        lineError("Add at least one line item.");
    }
    if (lines.some((l) => (l.unitType?.length ?? 0) > UnitType.MAX)) {
        lineError(`Unit can't be over ${UnitType.MAX} characters.`);
    }
    if (lines.some((l) => isOutOfRange(l.quantity, Quantity))) {
        lineError("A quantity is out of range.");
    }
    if (lines.some((l) => isOutOfRange(l.estimatedUnitCost, Currency))) {
        lineError("A unit cost is out of range.");
    }
    return errors;
};

export interface IEstimateUsage {
    lineCount: number;
    costCodeCount: number;
    total: number;
}

export const getEstimateUsage = (suggestions: IBidPackageSuggestion[]): IEstimateUsage => {
    const lines = suggestions.flatMap((s) => s.draft.lineItems);
    return {
        lineCount: lines.length,
        costCodeCount: new Set(lines.map((line) => line.costCode.id)).size,
        total: suggestions.reduce((sum, s) => sum + getPackageEstimateTotal(s.draft), 0),
    };
};

export const getDistinctCostCodes = (draft: IBidPackageDraft): string[] => [
    ...new Set(draft.lineItems.map((line) => line.costCode.name)),
];

export const formatSubInsight = (insight: ISubBidInsight | null): string | undefined => {
    if (!insight) {
        return undefined;
    }
    if (insight.recentBidsTotal === 0) {
        return "First bid with you";
    }
    return `Won ${insight.recentBidsAwarded} of ${insight.recentBidsTotal} recent bids`;
};

export const formatDeadline = (deadline: BidPackageDraftDeadline | null): string => {
    if (!deadline) {
        return "No deadline";
    }
    const time = deadline.time ? ` at ${deadline.time}` : "";
    if (deadline.kind === "fixed") {
        return `${deadline.date}${time}`;
    }
    const days = Math.abs(deadline.offsetDays);
    const direction = deadline.offsetDays < 0 ? "before" : "after";
    return `${days} ${days === 1 ? "day" : "days"} ${direction} ${deadline.scheduleItem.name}${time}`;
};

export const estimateLineIds = (draft: IBidPackageDraft): number[] =>
    draft.lineItems.flatMap((line) =>
        line.sourceEstimateLineItemId === null ? [] : [line.sourceEstimateLineItemId],
    );

/**
 * The chat turn for what the builder sees: every package as edited, plus the lines that must not
 * come back (removed by the builder in this or an earlier turn, or already on a bid package
 * created from this page).
 */
export const buildRefineMessage = ({
    jobId,
    instruction,
    shown,
    original,
    editedKeys,
    createdKeys,
    excludedLineIds = new Set(),
}: {
    jobId: number;
    instruction: string;
    /** The packages on screen, edits applied, created ones included. */
    shown: IBidPackageSuggestion[];
    /** The agent's last answer, before removals and edits. */
    original: IBidPackageSuggestion[];
    editedKeys: ReadonlySet<string>;
    createdKeys: ReadonlySet<string>;
    /** Lines excluded by earlier turns; the agent's last answer no longer lists them. */
    excludedLineIds?: ReadonlySet<number>;
}): IRefineBidPackagesMessage => {
    const onScreen = shown.filter((s) => !createdKeys.has(s.suggestionKey));
    const kept = new Set(onScreen.flatMap((s) => estimateLineIds(s.draft)));
    return {
        jobId,
        instruction,
        currentPackages: onScreen.map((s) => ({
            suggestionKey: s.suggestionKey,
            title: s.draft.title,
            description: s.draft.description,
            edited: editedKeys.has(s.suggestionKey),
            lines: s.draft.lineItems.flatMap((line) =>
                line.sourceEstimateLineItemId === null
                    ? []
                    : [
                          {
                              estimateLineId: line.sourceEstimateLineItemId,
                              quantity: line.quantity,
                              unit: line.unitType,
                              unitCost: line.estimatedUnitCost,
                          },
                      ],
            ),
        })),
        excludedEstimateLineIds: [
            ...new Set([...excludedLineIds, ...original.flatMap((s) => estimateLineIds(s.draft))]),
        ].filter((id) => !kept.has(id)),
    };
};

/**
 * Estimate lines that aren't in any package on screen: the ones the agent left out, plus any the
 * builder removed since. Lines on a package already created count as included.
 */
export const getLeftOutLines = (
    response: IBidPackageSuggestionResponse,
    shown: IBidPackageSuggestion[],
): IBidPackageDraftLineItem[] => {
    const included = new Set(shown.flatMap((s) => estimateLineIds(s.draft)));
    const leftOut = new Map<number, IBidPackageDraftLineItem>();
    const candidates = [
        ...(response.excludedEstimateLines ?? []),
        ...response.suggestions.flatMap((s) => s.draft.lineItems),
    ];
    candidates.forEach((line) => {
        const id = line.sourceEstimateLineItemId;
        if (id !== null && !included.has(id) && !leftOut.has(id)) {
            leftOut.set(id, line);
        }
    });
    return [...leftOut.values()];
};
